import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CustomSelect } from "@/components/ui/custom-select";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { FilterChip } from "@/components/ui/filter-chip";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Icon } from "@/components/ui/icons";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { PageTitle } from "@/contexts/page-title-context";
import {
  SortableHeaderCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { CompactCardSkeleton } from "@/components/ui/compact-card-skeleton";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useSizeClass } from "@/hooks/use-size-class";
import { useSortableTable } from "@/hooks/use-sortable-table";
import {
  getAdminApplications,
  searchAdminApplications,
} from "@/lib/api/admin/applications";
import type { AdminApplication } from "@/lib/api/admin/types";
import { ApplicationStatusBadge } from "@/components/admin/application-status";
import {
  formatAdminDate,
  formatAdminEntityTypeLabel,
} from "@/components/admin/formatting";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { highlight } from "@/lib/highlight";
import { applicationSearchableFields } from "@/lib/search/application";

type StatusFilter = "All" | "Pending" | "Approved" | "Rejected";
type EntityFilter =
  | "All"
  | "Shipper"
  | "TransportCompany"
  | "Broker"
  | "Driver";

function AdminApplicationsPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { refresh?: string };
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminApplications } = useTranslation("admin.applications");
  const { t: tAdminEntityType } = useTranslation(
    "admin.applications.entityType",
  );
  const { t: tCommon } = useTranslation("common");
  const refreshToken = search.refresh ?? "";
  const { filters, setFilter, clearFilters } = useFilterSearchParams({
    keys: ["search", "status", "entity_type"],
    defaults: { status: "All", entity_type: "All" },
  });
  const searchQuery = filters.search;
  const statusFilter = filters.status as StatusFilter;
  const entityFilter = filters.entity_type as EntityFilter;

  const { offset } = usePageOffset();
  const isApprovedParam =
    statusFilter === "Pending"
      ? false
      : statusFilter === "Approved"
        ? true
        : undefined;
  const {
    data,
    isLoading: queryLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      ...queryKeys.admin.applications({
        limit: DEFAULT_PAGE_SIZE,
        offset,
        status: statusFilter,
      }),
      refreshToken,
    ],
    queryFn: () =>
      getAdminApplications({
        limit: DEFAULT_PAGE_SIZE,
        offset,
        is_approved: isApprovedParam,
      }),
  });
  const [searchResults, setSearchResults] = useState<AdminApplication[] | null>(
    null,
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const loading = searchLoading || queryLoading;
  const applications = useMemo(
    () => searchResults ?? data?.data ?? [],
    [searchResults, data?.data],
  );
  const total = data?.total ?? 0;

  const { page, totalPages, setPage } = usePagination({ total });

  // Page reset on filter change handled by useFilterSearchParams

  const filteredApplications = useMemo(() => {
    return applications
      .filter((app) => {
        if (entityFilter === "All") return true;
        return app.entity_type === entityFilter;
      })
      .filter((app) => {
        if (statusFilter === "All") return true;
        if (statusFilter === "Pending") return app.is_approved === false;
        if (statusFilter === "Approved") return app.is_approved === true;
        return (
          app.status === "Rejected" || app.status === "RejectedTemporarily"
        );
      });
  }, [applications, entityFilter, statusFilter]);

  const {
    sort,
    toggleSort,
    sortedData: sortedApplications,
  } = useSortableTable(filteredApplications, { defaultColumn: "name" });

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchResults(null);
      setSearchLoading(false);
      setSearchError(false);
      return;
    }
    let didCancel = false;
    setSearchLoading(true);
    setSearchError(false);
    (async () => {
      try {
        const results = await searchAdminApplications({ query, language });
        if (!didCancel) {
          setSearchResults(results);
        }
      } catch (error) {
        console.error("Failed to search applications", error);
        if (!didCancel) {
          setSearchError(true);
        }
      } finally {
        if (!didCancel) {
          setSearchLoading(false);
        }
      }
    })();
    return () => {
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is stable
  }, [searchQuery]);

  const statusOptions = [
    { value: "All", label: tAdminApplications("status.all") },
    { value: "Pending", label: tAdminApplications("status.pending") },
    { value: "Approved", label: tAdminApplications("status.approved") },
    { value: "Rejected", label: tAdminApplications("status.rejected") },
  ];
  const entityOptions = [
    { value: "All", label: tAdminEntityType("all") },
    { value: "Shipper", label: tAdminEntityType("shipper") },
    {
      value: "TransportCompany",
      label: tAdminEntityType("carrier"),
    },
    { value: "Broker", label: tAdminEntityType("broker") },
    { value: "Driver", label: tAdminEntityType("driver") },
  ];

  return (
    <VStack gap="lg" data-testid="admin-applications-page">
      <PageTitle>{tAdminApplications("title")}</PageTitle>
      <VStack
        gap="none"
        data-testid="admin-applications-header"
        className="sticky top-16 z-sticky"
      >
        <Card
          data-testid="admin-applications-header-actions-card"
          variant="bordered"
          padding="xs"
        >
          <FilterToolbar
            searchPlaceholder={tAdminApplications("searchPlaceholder")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            quickFilters={
              <>
                {statusOptions.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    active={statusFilter === opt.value}
                    onClick={() => setFilter("status", opt.value)}
                    data-testid={`admin-applications-filter-status-${opt.value}`}
                  >
                    {opt.label}
                  </FilterChip>
                ))}
              </>
            }
            advancedFilters={[
              <CustomSelect
                key="entity-filter"
                data-testid="admin-applications-entity-filter"
                value={entityFilter}
                onChange={(value) => setFilter("entity_type", value)}
                options={entityOptions}
                label={tAdminApplications("entityTypeLabel")}
              />,
            ]}
            activeFilterCount={entityFilter !== "All" ? 1 : 0}
            onClearFilters={clearFilters}
            data-testid="admin-applications-toolbar"
          />
        </Card>
      </VStack>

      <Card data-testid="admin-applications-card" padding="none">
        <TableViewport position="relative">
          {loading && applications.length === 0 ? (
            isCompact ? (
              <CompactCardSkeleton />
            ) : (
              <TableSkeleton columns={5} rows={5} />
            )
          ) : isError || searchError ? (
            <ErrorState
              title={tAdminApplications("failedToLoadApplications")}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void refetch()}
                >
                  {tCommon("retry")}
                </Button>
              }
              className="py-16"
            />
          ) : sortedApplications.length === 0 ? (
            <EmptyState
              icon={<Icon.Search size="xl" />}
              title={tAdminApplications("applicationsTableTitle")}
              description={tAdminApplications("noApplicationsFound")}
              className="py-16"
            />
          ) : isCompact ? (
            <DividedList>
              {sortedApplications.map((app) => (
                <InteractiveCard
                  key={app.id}
                  data-testid={`application-card-${app.id}`}
                  onClick={() =>
                    void navigate({
                      to: "/admin/applications/$id",
                      params: { id: app.id },
                    })
                  }
                >
                  <HStack justify="between" gap="none">
                    <Text weight="semibold">
                      {highlight(
                        applicationSearchableFields(app, language).name,
                        searchQuery,
                      )}
                    </Text>
                    <ApplicationStatusBadge application={app} />
                  </HStack>
                  <Text size="xs" color="muted">
                    {formatAdminEntityTypeLabel(
                      app.entity_type,
                      tAdminEntityType,
                    )}
                    {" \u00b7 "}
                    {formatAdminDate(app.created_at, language)}
                  </Text>
                </InteractiveCard>
              ))}
            </DividedList>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell
                    column="name"
                    sortColumn={sort?.column}
                    sortDirection={sort?.direction}
                    onSort={toggleSort}
                  >
                    {tAdminApplications("columns.companyOrName")}
                  </SortableHeaderCell>
                  <TableHeaderCell>
                    {tAdminApplications("columns.entityType")}
                  </TableHeaderCell>
                  <SortableHeaderCell
                    column="created_at"
                    sortColumn={sort?.column}
                    sortDirection={sort?.direction}
                    onSort={toggleSort}
                  >
                    {tAdminApplications("columns.submitted")}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    column="status"
                    sortColumn={sort?.column}
                    sortDirection={sort?.direction}
                    onSort={toggleSort}
                  >
                    {tAdminApplications("columns.status")}
                  </SortableHeaderCell>
                  <TableHeaderCell className="text-right">
                    {tAdminApplications("columns.actions")}
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="font-medium">
                      {highlight(
                        applicationSearchableFields(app, language).name,
                        searchQuery,
                      )}
                    </TableCell>
                    <TableCell>
                      {formatAdminEntityTypeLabel(
                        app.entity_type,
                        tAdminEntityType,
                      )}
                    </TableCell>
                    <TableCell>
                      {formatAdminDate(app.created_at, language)}
                    </TableCell>
                    <TableCell>
                      <ApplicationStatusBadge application={app} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        color="neutral"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void navigate({
                            to: "/admin/applications/$id",
                            params: { id: app.id },
                          })
                        }
                      >
                        {tCommon("view")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {loading && applications.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/applications/")({
  component: AdminApplicationsPage,
});
